import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Prayer } from '../../types';

const CHURCH_SLUG = 'brisas';
const BG = '#1C1C1E';
const CARD = '#2C2C2E';
const CARD2 = '#3A3A3C';

function SkeletonCard() {
  return (
    <div className="rounded-lg p-4 animate-pulse" style={{ backgroundColor: CARD }}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full" style={{ backgroundColor: CARD2 }} />
        <div className="flex-1 space-y-2">
          <div className="h-4 rounded" style={{ backgroundColor: CARD2, width: '70%' }} />
          <div className="h-3 rounded" style={{ backgroundColor: CARD2 }} />
          <div className="h-3 rounded" style={{ backgroundColor: CARD2, width: '30%' }} />
        </div>
      </div>
    </div>
  );
}

export default function Prayers() {
  const [prayers, setPrayers] = useState<Prayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [churchId, setChurchId] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('churches').select('id').eq('slug', CHURCH_SLUG).single()
      .then(({ data }) => { if (data) setChurchId(data.id); });
  }, []);

  useEffect(() => {
    if (!churchId) return;
    setLoading(true);
    supabase.from('prayers').select('*')
      .eq('church_id', churchId).eq('is_public', true)
      .order('created_at', { ascending: false }).limit(30)
      .then(({ data }) => { setPrayers(data || []); setLoading(false); });
  }, [churchId]);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-5" style={{ background: 'linear-gradient(180deg, #0D2B8E 0%, #1C1C1E 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Peticiones</h1>
            <p className="text-sm mt-0.5" style={{ color: '#4A90D9' }}>Oración colectiva · Lun–Vie 8 AM</p>
          </div>
          <Link
            to="/prayers/new"
            className="w-11 h-11 flex items-center justify-center rounded-lg text-white text-2xl font-bold"
            style={{ backgroundColor: '#4A90D9' }}
          >
            +
          </Link>
        </div>
      </div>

      {/* Lista */}
      <div className="p-4 space-y-3">
        {loading ? (
          <><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
        ) : prayers.length === 0 ? (
          <div className="rounded-lg p-10 text-center" style={{ backgroundColor: CARD }}>
            <p className="text-4xl mb-3">🙏</p>
            <p className="text-white">No hay peticiones públicas aún.</p>
            <p className="text-sm mt-1" style={{ color: '#8E8E93' }}>Sé el primero en compartir.</p>
          </div>
        ) : (
          prayers.map((prayer) => (
            <Link
              key={prayer.id}
              to={`/prayers/${prayer.id}`}
              className="block rounded-lg p-4"
              style={{ backgroundColor: CARD }}
            >
              <div className="flex items-start gap-3">
                <span className="text-xl mt-0.5">{prayer.is_answered ? '✅' : '🙏'}</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">{prayer.title}</h3>
                  <p className="text-sm mt-1 line-clamp-2" style={{ color: '#8E8E93' }}>{prayer.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs" style={{ color: '#8E8E93' }}>{formatDate(prayer.created_at)}</span>
                    {prayer.is_answered && (
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#3A3A3C', color: '#30D158' }}>
                        Respondida
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
